import { ChevronDown } from 'lucide-react-native';
import { StyleSheet, Text, View } from 'react-native';
import { SvgXml } from 'react-native-svg';

import { Fonts, Palette } from '@/constants/theme';
import { LOGO_SVG_XML } from '@/generated/logo-svg-data';

/**
 * Homestead mark + wordmark.
 *
 * The mark is the branded `icon-dark.svg` illustration (high-contrast
 * forest house with a honey sun), inlined into the JS bundle by
 * scripts/inline-icon-svg.mjs so it appears the instant the component
 * mounts — no second network roundtrip. The wordmark uses Fraunces
 * semibold to echo the rest of the app's serif voice; the secondary
 * "COACH" microtext is set in Hanken Grotesk to stay structurally
 * distinct from the brand name itself.
 *
 * Replacing the previous hand-rolled `<Path>`/`<Circle>` mark with the
 * full branded illustration: same footprint, dramatically more contrast
 * and detail. SvgXml renders identically on iOS, Android, and Web.
 */
export interface LogoProps {
  /** Pixel height of the badge. The wordmark scales relative to this. */
  size?: number;
  /** Hide the text wordmark and render only the mark. */
  iconOnly?: boolean;
  /** Hide the "COACH" sub-wordmark — useful in tight spots. */
  hideSubmark?: boolean;
  /**
   * When set (multi-home accounts), replaces the "Homestead" wordmark with
   * the active home's name + a chevron-down to telegraph the switcher.
   * The mark stays. The parent <Pressable> is responsible for actually
   * opening the switcher modal — this prop only changes the visual.
   */
  homeName?: string | null;
  /**
   * Render the LogoMark icon. Default true. Pass false when the caller
   * is rendering the mark themselves (e.g. AppHeader split layout where
   * the mark lives in its own Pressable so it can navigate Home
   * independently of the switcher trigger).
   */
  mark?: boolean;
}

export function Logo({
  size = 32,
  iconOnly = false,
  hideSubmark = false,
  homeName = null,
  mark = true,
}: LogoProps) {
  const switcherMode = Boolean(homeName);
  return (
    <View style={styles.row}>
      {mark ? <LogoMark size={size} /> : null}
      {iconOnly ? null : switcherMode ? (
        // Multi-home mode: the brand area becomes a switcher trigger.
        // Same serif voice as the wordmark so the swap doesn't read as a
        // jarring different-component; chevron-down is the affordance.
        <View style={[styles.switcher, { marginLeft: 8 }]}>
          <Text
            style={[styles.homeName, { fontSize: size * 0.52 }]}
            numberOfLines={1}>
            {homeName}
          </Text>
          <ChevronDown
            size={Math.max(14, size * 0.42)}
            color={Palette.inkSoft}
            strokeWidth={2.2}
          />
        </View>
      ) : (
        <View style={{ marginLeft: 8 }}>
          <Text style={[styles.wordmark, { fontSize: size * 0.6 }]}>Homestead</Text>
          {hideSubmark ? null : (
            <Text style={[styles.submark, { fontSize: Math.max(8.5, size * 0.26) }]}>
              COACH
            </Text>
          )}
        </View>
      )}
    </View>
  );
}

export function LogoMark({ size = 32 }: { size?: number }) {
  return <SvgXml xml={LOGO_SVG_XML} width={size} height={size} />;
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  wordmark: {
    fontFamily: Fonts.serifBold,
    color: Palette.ink,
    letterSpacing: -0.4,
    lineHeight: undefined,
  },
  submark: {
    marginTop: -2,
    fontFamily: Fonts.sansBold,
    color: Palette.honeyDeep,
    letterSpacing: 2.4,
  },
  // Multi-home switcher row — home name + chevron. Centered vertically so
  // the chevron sits on the visual baseline of the home name.
  switcher: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    // Bound the home-name text so very long names ellipsize instead of
    // pushing the right-side header controls off-screen.
    maxWidth: 220,
  },
  homeName: {
    fontFamily: Fonts.serifBold,
    color: Palette.ink,
    letterSpacing: -0.3,
  },
});
